// src/app/router.tsx
import {
  createRouter,
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  redirect,
  Outlet,
  Navigate,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { isAuthenticated } from '@/auth/guards';
import { useAuthStore } from '@/auth/auth-store';
import { capabilitiesQuery, verdictOf } from '@/auth/capabilities';
import { canOpen } from '@/auth/section-access';
import type { MyCapabilities } from '@/api/schemas/me';
import { NAV_GROUPS } from '@/components/layout/nav-items';
import { queryClient } from '@/lib/query-client';
import { AppShell } from '@/components/layout/app-shell';
import { LoginPage } from '@/features/auth/login-page';
const RegisterPage = lazyRouteComponent(
  () => import('@/features/auth/register-page'),
  'RegisterPage',
);
const AnticheatPage = lazyRouteComponent(
  () => import('@/features/anticheat/anticheat-page'),
  'AnticheatPage',
);
const TiendaHomePage = lazyRouteComponent(
  () => import('@/features/tienda/tienda-home-page'),
  'TiendaHomePage',
);
const TiendaListingsPage = lazyRouteComponent(
  () => import('@/features/tienda/tienda-list-pages'),
  'TiendaListingsPage',
);
const TiendaOrdersPage = lazyRouteComponent(
  () => import('@/features/tienda/tienda-list-pages'),
  'TiendaOrdersPage',
);
const TiendaKoHubPage = lazyRouteComponent(
  () => import('@/features/tienda/tienda-list-pages'),
  'TiendaKoHubPage',
);
const TiendaPayoutsPage = lazyRouteComponent(
  () => import('@/features/tienda/tienda-list-pages'),
  'TiendaPayoutsPage',
);
const TiendaProfilePage = lazyRouteComponent(
  () => import('@/features/tienda/tienda-profile-page'),
  'TiendaProfilePage',
);
const InvitationsPage = lazyRouteComponent(
  () => import('@/features/invitations/invitations-page'),
  'InvitationsPage',
);
const DashboardPage = lazyRouteComponent(
  () => import('@/features/dashboard/dashboard-page'),
  'DashboardPage',
);
const UsersListPage = lazyRouteComponent(
  () => import('@/features/users/users-list-page'),
  'UsersListPage',
);
const UserDetailPage = lazyRouteComponent(
  () => import('@/features/users/user-detail-page'),
  'UserDetailPage',
);
const ReviewQueuePage = lazyRouteComponent(
  () => import('@/features/listings/review-queue-page'),
  'ReviewQueuePage',
);
const ReviewDetailPage = lazyRouteComponent(
  () => import('@/features/listings/review-detail-page'),
  'ReviewDetailPage',
);
const TournamentsListPage = lazyRouteComponent(
  () => import('@/features/tournaments/tournaments-list-page'),
  'TournamentsListPage',
);
const TournamentDetailPage = lazyRouteComponent(
  () => import('@/features/tournaments/tournament-detail-page'),
  'TournamentDetailPage',
);
const CreateTournamentPage = lazyRouteComponent(
  () => import('@/features/tournaments/create-tournament-page'),
  'CreateTournamentPage',
);
const DisputesListPage = lazyRouteComponent(
  () => import('@/features/disputes/disputes-list-page'),
  'DisputesListPage',
);
const DisputeDetailPage = lazyRouteComponent(
  () => import('@/features/disputes/dispute-detail-page'),
  'DisputeDetailPage',
);
const OrdersListPage = lazyRouteComponent(
  () => import('@/features/orders/orders-list-page'),
  'OrdersListPage',
);
const OrderDetailPage = lazyRouteComponent(
  () => import('@/features/orders/order-detail-page'),
  'OrderDetailPage',
);
const TradesPage = lazyRouteComponent(() => import('@/features/trades/trades-page'), 'TradesPage');
const TradeDetailPage = lazyRouteComponent(
  () => import('@/features/trades/trade-detail-page'),
  'TradeDetailPage',
);
const PayoutsListPage = lazyRouteComponent(
  () => import('@/features/wallet/payouts-list-page'),
  'PayoutsListPage',
);
const PayoutDetailPage = lazyRouteComponent(
  () => import('@/features/wallet/payout-detail-page'),
  'PayoutDetailPage',
);
const FrozenEscrowPage = lazyRouteComponent(
  () => import('@/features/escrow/frozen-escrow-page'),
  'FrozenEscrowPage',
);
const StuckOutboxPage = lazyRouteComponent(
  () => import('@/features/outbox/stuck-outbox-page'),
  'StuckOutboxPage',
);
const PendingDecisionsPage = lazyRouteComponent(
  () => import('@/features/pending-decisions/pending-decisions-page'),
  'PendingDecisionsPage',
);
const ModerationQueuePage = lazyRouteComponent(
  () => import('@/features/moderation/moderation-queue-page'),
  'ModerationQueuePage',
);
const ReportDetailPage = lazyRouteComponent(
  () => import('@/features/moderation/report-detail-page'),
  'ReportDetailPage',
);
const StaffListPage = lazyRouteComponent(
  () => import('@/features/staff/staff-list-page'),
  'StaffListPage',
);
const StaffDetailPage = lazyRouteComponent(
  () => import('@/features/staff/staff-detail-page'),
  'StaffDetailPage',
);
const KoinsPage = lazyRouteComponent(() => import('@/features/koins/koins-page'), 'KoinsPage');
const TiersPage = lazyRouteComponent(() => import('@/features/tiers/tiers-page'), 'TiersPage');
const QuestionsListPage = lazyRouteComponent(
  () => import('@/features/questions/questions-list-page'),
  'QuestionsListPage',
);
const QuestionCreatePage = lazyRouteComponent(
  () => import('@/features/questions/question-form-page'),
  'QuestionCreatePage',
);
const QuestionDetailPage = lazyRouteComponent(
  () => import('@/features/questions/question-detail-page'),
  'QuestionDetailPage',
);
const ProposalsPage = lazyRouteComponent(
  () => import('@/features/questions/proposals-page'),
  'ProposalsPage',
);
const QuestionBanksPage = lazyRouteComponent(
  () => import('@/features/question-banks/question-banks-page'),
  'QuestionBanksPage',
);
const ConfigPage = lazyRouteComponent(() => import('@/features/config/config-page'), 'ConfigPage');
const ReportsPage = lazyRouteComponent(
  () => import('@/features/reports/reports-page'),
  'ReportsPage',
);
const TaxonomyPage = lazyRouteComponent(
  () => import('@/features/taxonomy/taxonomy-page'),
  'TaxonomyPage',
);
const PackageProfilesPage = lazyRouteComponent(
  () => import('@/features/package-profiles/package-profiles-page'),
  'PackageProfilesPage',
);
const CampaignsPage = lazyRouteComponent(
  () => import('@/features/campaigns/campaigns-page'),
  'CampaignsPage',
);
const StrikesPage = lazyRouteComponent(
  () => import('@/features/strikes/strikes-page'),
  'StrikesPage',
);
const ReferralsPage = lazyRouteComponent(
  () => import('@/features/referrals/referrals-page'),
  'ReferralsPage',
);
const KycQueuePage = lazyRouteComponent(
  () => import('@/features/kyc/kyc-queue-page'),
  'KycQueuePage',
);
const PrizesPage = lazyRouteComponent(() => import('@/features/prizes/prizes-page'), 'PrizesPage');
const SecurityPage = lazyRouteComponent(
  () => import('@/features/security/security-page'),
  'SecurityPage',
);
const CreatorsListPage = lazyRouteComponent(
  () => import('@/features/creators/creators-list-page'),
  'CreatorsListPage',
);
const StoresListPage = lazyRouteComponent(
  () => import('@/features/stores/stores-list-page'),
  'StoresListPage',
);
const StoreDetailPage = lazyRouteComponent(
  () => import('@/features/stores/store-detail-page'),
  'StoreDetailPage',
);
const KoHubListPage = lazyRouteComponent(
  () => import('@/features/kohub/kohub-list-page'),
  'KoHubListPage',
);
const KoHubDetailPage = lazyRouteComponent(
  () => import('@/features/kohub/kohub-detail-page'),
  'KoHubDetailPage',
);
const CreatorDetailPage = lazyRouteComponent(
  () => import('@/features/creators/creator-detail-page'),
  'CreatorDetailPage',
);

const AuditPage = lazyRouteComponent(() => import('@/features/audit/audit-page'), 'AuditPage');

/**
 * The router shares the app's QueryClient so the capabilities it waits for
 * before a section loads are the very cache entry the sidebar and the buttons
 * read — one request per session, not one per reader.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>) => ({
    redirect: typeof s.redirect === 'string' ? s.redirect : undefined,
  }),
  component: LoginPage,
});

// Registro público de admin por invitación (fuera del guard de auth).
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: RegisterPage,
});

/**
 * What the signed-in operator may do, fetched once and cached (ko-admin#190).
 *
 * A failure answers `undefined`, which every verdict reads as `unknown` and
 * `canOpen` lets through: this gate is not the permission boundary — the
 * backend is — and a request that failed must not lock anybody out of the panel.
 */
async function capabilitiesFor(
  qc: QueryClient,
  who: string | null,
): Promise<MyCapabilities | undefined> {
  try {
    return await qc.ensureQueryData(capabilitiesQuery(who));
  } catch {
    return undefined;
  }
}

/**
 * Where to send somebody who typed a path they may not open, or `null` to let
 * them in. It reads the same `SECTION_CAPABILITY` map the sidebar paints from,
 * so the menu and the URL bar cannot disagree.
 *
 * An ADMIN is let in without asking anything: that role opens every section.
 * A STAFF member waits for the capabilities answer — deciding on the in-flight
 * state would either flash a section the backend refuses or bounce somebody out
 * of one they may open. The destination is the first section of the menu they
 * CAN open (Seguridad, at worst, is open to every staff member), never a dead
 * end. A role that is neither is left to the backend, as before.
 */
export async function sectionRedirect(pathname: string, qc: QueryClient): Promise<string | null> {
  const user = useAuthStore.getState().user;
  // 2026-11-02 — a store owner (role USER) opens their own sections and the
  // second factor, nothing else: the Panel and every admin screen send them
  // to their store's home, without asking the capabilities route (which is
  // not theirs).
  if (user?.role === 'USER') {
    return canOpen(pathname, user.role, () => 'unknown') ? null : '/tienda';
  }
  // An ADMIN opens every section of the platform; the store owner's screens
  // are the one place that is not theirs (they read stores from `/stores`).
  if (user?.role === 'ADMIN') {
    return canOpen(pathname, user.role, () => 'unknown') ? null : '/';
  }
  if (user?.role !== 'STAFF') return null;
  const data = await capabilitiesFor(qc, user.id);
  const verdict = (c: string) => verdictOf(data, c);
  if (canOpen(pathname, user.role, verdict)) return null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (canOpen(item.to, user.role, verdict)) return item.to;
    }
  }
  return null;
}

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ location, context }) => {
    if (!isAuthenticated())
      throw redirect({ to: '/login', search: { redirect: location.pathname } });
    const to = await sectionRedirect(location.pathname, context.queryClient);
    if (to !== null) throw redirect({ to });
  },
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: DashboardPage,
});

// The store owner's panel (2026-11-02): their own store, nothing of the platform.
const tiendaHomeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tienda',
  component: TiendaHomePage,
});
const tiendaListingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tienda/publicaciones',
  component: TiendaListingsPage,
});
const tiendaOrdersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tienda/pedidos',
  component: TiendaOrdersPage,
});
const tiendaKoHubRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tienda/kohub',
  component: TiendaKoHubPage,
});
const tiendaPayoutsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tienda/cobros',
  component: TiendaPayoutsPage,
});
const tiendaProfileRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tienda/perfil',
  component: TiendaProfilePage,
});

const userDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users/$id',
  component: UserDetailPage,
});

const usersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users',
  component: UsersListPage,
});

const listingsDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/listings/$id',
  component: ReviewDetailPage,
});

const listingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/listings',
  component: ReviewQueuePage,
});

const tournamentNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tournaments/new',
  component: CreateTournamentPage,
});

const tournamentDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tournaments/$id',
  component: TournamentDetailPage,
});

const tournamentsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tournaments',
  component: TournamentsListPage,
});

const disputesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/disputes',
  component: DisputesListPage,
});

const disputeDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/disputes/$id',
  component: DisputeDetailPage,
});

// #83 — user-report moderation queue + detail. `/moderation`, not
// `/reports`: that path is the panel's analytics section.
const moderationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/moderation',
  component: ModerationQueuePage,
});

const moderationDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/moderation/$id',
  component: ReportDetailPage,
});

// ko-backend#733 — the frozen-escrow queue. A route of its own for the same
// reason Intercambios has one: these are exactly the orders with NO dispute,
// and the only surface that reached unfreeze was a dispute detail.
const frozenEscrowRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/escrow',
  component: FrozenEscrowPage,
});

// ko-backend#800 — the outbox's dead letters. A route of its own for the same
// reason as /escrow: rows the platform will never look at again on its own,
// and no other screen could reach them. ADMIN + MFA, no staff capability: a
// requeue moves no money and creates nothing.
const stuckOutboxRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/outbox',
  component: StuckOutboxPage,
});
// ko-backend#777 — money outcomes the platform left waiting on a person. A
// route of its own next to the frozen-escrow queue: it is the same question
// (money that will not move by itself) and, like that queue, nothing else in
// the panel reached these rows.
const pendingDecisionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/pending-decisions',
  component: PendingDecisionsPage,
});

// ko-admin#145 — the orders section. `GET /admin/orders` has accepted all eight
// statuses since ko-admin#42 and the only listing was the shipment-compliance
// tab, so an order that was neither late nor disputed could only be looked at
// with SQL. The detail is declared AFTER the list for the same reason the
// backend declares `:id` last: any future EXACT-path route under `/orders` has
// to go above it.
const ordersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/orders',
  component: OrdersListPage,
});

const orderDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/orders/$id',
  component: OrderDetailPage,
});

// ko-admin#152 — payouts. The money LEAVING the platform had no screen at all:
// `Payout` rows were written by four services and read by nobody but analytics,
// which returns a SUM and a COUNT. The detail is declared AFTER the list, the
// same rule the backend controller documents: any future EXACT-path route under
// `/payouts` has to go above the `$id` one or it will be matched as an id.
const payoutsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/payouts',
  // ko-admin#213 — the Panel opens it on the pending ones. Optional in the
  // TYPE too, so the plain links to the list keep compiling without `search`.
  validateSearch: (s: Record<string, unknown>): { status?: string } => ({
    status: typeof s.status === 'string' ? s.status : undefined,
  }),
  component: PayoutsListPage,
});

const payoutDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/payouts/$id',
  component: PayoutDetailPage,
});

// Cola de revisión manual de intercambios + listado con filtros (#117).
const tradesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/trades',
  component: TradesPage,
});

// #185 — one trade, with the admin panel that until now only a dispute detail
// mounted. A trade held WITHOUT a dispute (antifraud BLOCK, high value) had no
// screen from which to reject it. Declared AFTER the list, like the other
// details: any future EXACT-path route under `/trades` goes above it.
const tradeDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/trades/$id',
  component: TradeDetailPage,
});

const staffRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/staff',
  component: StaffListPage,
});

const staffDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/staff/$userId',
  component: StaffDetailPage,
});

const tiersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tiers',
  component: TiersPage,
});

const koinsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/koins',
  validateSearch: (s: Record<string, unknown>) => ({
    userId: typeof s.userId === 'string' ? s.userId : undefined,
    tab: typeof s.tab === 'string' ? s.tab : undefined,
  }),
  component: KoinsPage,
});

// Questions (A-08).
const questionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/questions',
  component: QuestionsListPage,
});
const questionsNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/questions/new',
  component: QuestionCreatePage,
});
// ko-backend#892 — the queue's filter and the row to open travel in the search
// string, so the «Reenviada» chip can link to the rejected original a
// resubmission replaces. `status` is checked against the enum by the page.
// Both keys are optional in the TYPE as well, so the plain links to the queue
// keep compiling without a `search` prop.
const questionsProposalsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/questions/proposals',
  validateSearch: (s: Record<string, unknown>): { status?: string; focus?: string } => ({
    status: typeof s.status === 'string' ? s.status : undefined,
    focus: typeof s.focus === 'string' ? s.focus : undefined,
  }),
  component: ProposalsPage,
});
const questionDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/questions/$id',
  component: QuestionDetailPage,
});

// Banks + question taxonomy. Its own top-level path rather than
// /questions/banks so it never competes with /questions/$id.
const questionBanksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/question-banks',
  component: QuestionBanksPage,
});

const configRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/config',
  component: ConfigPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports',
  component: ReportsPage,
});

// Vista global de strikes (A-22). Estática: gana sobre /users/$id por especificidad.
const strikesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users/strikes',
  component: StrikesPage,
});

// Cola de revisión de referidos.
const referralsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/referrals',
  component: ReferralsPage,
});

// Cola de revisión manual KYC (#332). Estática: gana sobre /users/$id.
const kycQueueRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users/kyc',
  component: KycQueuePage,
});

// Prize delivery tracking global (A-23). Estática: gana sobre /tournaments/$id.
const prizesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tournaments/prizes',
  component: PrizesPage,
});

// Categorías & taxonomía (A-12).
const taxonomyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/taxonomy',
  component: TaxonomyPage,
});

// The package catalogue the labels are priced from, read-only.
const packageProfilesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/package-profiles',
  component: PackageProfilesPage,
});

// Campaign broadcast (A-14).
const campaignsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/campaigns',
  component: CampaignsPage,
});

// Moderación de creadores (#85): listado + detalle con sanciones.
const creatorsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/creators',
  component: CreatorsListPage,
});
const creatorDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/creators/$id',
  component: CreatorDetailPage,
});

// ko-backend#1090 — the verified-store queue and one application.
const storesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/stores',
  component: StoresListPage,
});
const storeDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/stores/$id',
  component: StoreDetailPage,
});

// KO Hub — the case queue and one case.
const kohubRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kohub',
  component: KoHubListPage,
});
const kohubDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kohub/$id',
  component: KoHubDetailPage,
});

// Invitaciones de admin (registro por código).
const invitationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/invitations',
  component: InvitationsPage,
});

// #60 — anti-cheat review of one quiz session.
const anticheatRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/anticheat',
  component: AnticheatPage,
});

// #195 — the global audit log. A top-level section and not a tab of Personal:
// `AuditEvent` records every sensitive decision the platform made, not what the
// staff did. ADMIN only, by `SECTION_CAPABILITY`.
const auditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/audit',
  component: AuditPage,
});

// Seguridad de la propia cuenta: re-enrolamiento MFA autenticado (#86).
const securityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/security',
  component: SecurityPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  appLayoutRoute.addChildren([
    dashboardRoute,
    usersRoute,
    strikesRoute,
    referralsRoute,
    kycQueueRoute,
    userDetailRoute,
    listingsRoute,
    listingsDetailRoute,
    tournamentsRoute,
    prizesRoute,
    tournamentNewRoute,
    tournamentDetailRoute,
    disputesRoute,
    disputeDetailRoute,
    frozenEscrowRoute,
    pendingDecisionsRoute,
    ordersRoute,
    orderDetailRoute,
    payoutsRoute,
    payoutDetailRoute,
    tradesRoute,
    tradeDetailRoute,
    moderationRoute,
    moderationDetailRoute,
    staffRoute,
    staffDetailRoute,
    koinsRoute,
    tiersRoute,
    questionsRoute,
    questionsNewRoute,
    questionsProposalsRoute,
    questionDetailRoute,
    questionBanksRoute,
    configRoute,
    reportsRoute,
    taxonomyRoute,
    packageProfilesRoute,
    campaignsRoute,
    creatorsRoute,
    creatorDetailRoute,
    storesRoute,
    storeDetailRoute,
    kohubRoute,
    kohubDetailRoute,
    invitationsRoute,
    anticheatRoute,
    securityRoute,
    tiendaHomeRoute,
    tiendaListingsRoute,
    tiendaOrdersRoute,
    tiendaKoHubRoute,
    tiendaPayoutsRoute,
    tiendaProfileRoute,
    stuckOutboxRoute,
    auditRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultNotFoundComponent: () => <Navigate to="/" />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
